import { find, range, flatMap, toArray, diff, toNumber,
         occurrences, Direction, Position, Index } from './utils';

export const enum CrosswordType {
  Regular,
  CluesInSquares
}

export const enum SquareType {
  Clues,
  Number,
  Block,
  Symbol,
  Blank
}

export interface GridSize {
  rows: number;
  cols: number;
}

export interface SquareModel {
  type: SquareType;
  value: any;
}

export interface SquareListItem {
  row: number;
  col: number;
  square: SquareModel;
}

export interface ClueListItem {
  number: number;
  clue: ClueModel;
}

export interface CrosswordState {
  solution: string;
  words: {
    ok: number;
    total: number;
  };
}

export interface CrosswordRecordingEvent {
  clueId: number;    // id of solved clue
  timestamp: number; // time of solving in UTC milliseconds
}

export interface CrosswordRecording {
  id: string;
  cluesSolved: Set<number>;
  events: CrosswordRecordingEvent[];
}

export class ClueModel {
  private _id: number;
  private _clue: string;
  private _squares: Index.t[];
  private _direction: Direction;
  private _origin: Index.t;
  private _length: number;

  constructor(id: number, clue: string, dir: Direction, origin: Index.t, len: number) {
    this._id = id;
    this._clue = clue;
    this._direction = dir;
    this._origin = origin;
    this._length = len;
    this._squares = this._createSquares(origin, len, dir);
  }

  get id(): number { return this._id; }
  get clue(): string { return this._clue; }
  get squares(): Index.t[] { return this._squares; }
  get direction(): Direction { return this._direction; }
  get origin(): Index.t { return this._origin; }
  get length(): number { return this._length; }

  public hasSquare(i: Index.t): boolean {
    return find<Index.t>(this._squares, ((s: Index.t) => Index.equal(s, i))) !== null;
  }

  /**
   * Calculate an estimate of the space required to display the clue text
   */
  public clueTextSize(): number {
    const SOFT_HYPHEN: string = '\u00AD';
    const MINIMUM_SCORE: number = 8;
    return Math.max(
      MINIMUM_SCORE,
      this.clue.length + occurrences(this.clue, SOFT_HYPHEN)
    );
  }

  private _createSquares(start: Index.t, length: number, dir: Direction): Index.t[] {
    let indexes: number[] = [];

    if (Direction.equal(dir, Direction.Vertical)) {
      indexes = range(start.value, start.value + (length * start.width), start.width);
    } else if (Direction.equal(dir, Direction.Horizontal)) {
      indexes = range(start.value, start.value + length);
    } else if (Direction.equal(dir, Direction.VerticalReverse)) {
      indexes = range(start.value, start.value - (length * start.width), -start.width);
    } else if (Direction.equal(dir, Direction.HorizontalReverse)) {
      indexes = range(start.value, start.value - length, -1);
    }

    return indexes.map((i: number) => Index.create(i, start.width));
  }
}

export class CluesModel implements SquareModel {
  private _clues: ClueModel[];

  constructor(clues: ClueModel[]) {
    this._clues = clues;
  }

  get type(): SquareType { return SquareType.Clues; }
  get value(): ClueModel[] { return this._clues; }

  public getClueById(id: number): ClueModel | null {
    return find<ClueModel>(this._clues, (clue: ClueModel) => clue.id === id);
  }
}

export class NumberModel implements SquareModel {
  private _number: number;
  private _clues: ClueModel[];

  constructor(num: number, clues: ClueModel[]) {
    this._number = num;
    this._clues = clues;
  }

  get type(): SquareType { return SquareType.Number; }
  get value(): ClueModel[] { return this._clues; }
  get number(): number { return this._number; }

  public getClueById(id: number): ClueModel | null {
    return find<ClueModel>(this._clues, (clue: ClueModel) => clue.id === id);
  }
}

export class SymbolModel implements SquareModel {
  private _symbol: string;

  constructor(symbol: string) {
    this._symbol = symbol;
  }

  get type(): SquareType { return SquareType.Symbol; }
  get value(): string { return this._symbol; }
}

export class BlockModel implements SquareModel {
  get type(): SquareType { return SquareType.Block; }
  get value(): void { return; }
}

export class BlankModel implements SquareModel {
  get type(): SquareType { return SquareType.Blank; }
  get value(): void { return; }
}

export class CrosswordModel {
  private _id: number;
  private _type: CrosswordType;
  private _wordCount: number;

  private _rows: number;
  private _cols: number;
  private _grid: SquareModel[];
  private _layout: string[];
  private _solution: string[];
  private _alphabet: string;

  private _cachedCluesByIndex: Map<number, ClueModel[]> | null = null;
  private _cachedState: CrosswordState | null = null;

  constructor(id: number) {
    this._id = id;
    this._grid = [];
  }

  get id(): number { return this._id };
  get layout(): string[] { return toArray(this._layout); }
  get size(): GridSize { return { 'rows': this._rows, 'cols': this._cols }; }
  get type(): CrosswordType { return this._type; }
  get wordCount(): number { return this._wordCount; }

  public getSquareList(): SquareListItem[] {
    return this._grid.reduce((acc: SquareListItem[], square: SquareModel, i: number) => {
      let [row, col]: Position = Index.to2D(Index.create(i, this._cols));
      acc.push({'row': row, 'col': col, 'square': square});
      return acc;
    }, []);
  }

  public getClueList(): ClueListItem[] {
    let squares: SquareModel[] = this.getAllClueSquares();
    return flatMap<SquareModel, ClueListItem>(squares, ((square: SquareModel) => {
      return square.value.map((clue: ClueModel) => {
        return {'number': (<NumberModel>square).number,
                'clue': clue };
      });
    }));
  }

  public isSolutionForClue(clue: Index.t[], attempt: ArrayLike<string>): boolean {
    return clue.map((x: Index.t) => x.value).reduce((acc: boolean, x: number) => {
      return acc && attempt[x] === this._solution[x];
    }, true);
  }

  /**
   * Check that the attempted solution provided for a clue is complete. That is,
   * all squares contains a letter, but we do not care if it matches the actual solution.
   */
  public isAttemptedSolutionForClue(clue: Index.t[], attempt: ArrayLike<string>): boolean {
    return clue.map((x: Index.t) => x.value).reduce((acc: boolean, x: number) => {
      return acc && attempt[x] !== '_';
    }, true);
  }

  /**
   * Retrieve a set of clues containing errors
   */
  public getErrors(squares: string[]): Set<ClueModel> {
    const valid: Function = (square: string, solution: string): boolean => {
      return solution === '_'       // solution is wildcard
          || square   !== '_'       // square is empty
          && square   === solution; // solution and square match
    };

    let errors: Set<ClueModel> = new Set<ClueModel>();
    for (let i: number = 0; i < squares.length; i++) {
      if (!valid(squares[i], this._solution[i])) {
        for (let clue of this.getCluesByIndex(Index.create(i, this._cols))) {
          errors.add(clue);
        }
      }
    }

    return errors;
  }

  /**
   * Retrieve the CrosswordState for the squares provided, assumed
   * to represent the entire crossword.
   */
  public getProgress(squares: string[]): CrosswordState {
    let correct: number;

    // Build a cache on first run. The change between CrosswordStates is
    // usually only one square, so comparing only the surrounding area
    // of the changed square is a lot faster.
    if (this._cachedState) {
      let previousSolution: string = this._cachedState.solution;
      let changes: number[] = diff<string>(squares, previousSolution);

      if (changes.length === 0) {
        return this._cachedState;
      }

      // TODO: Calling getCluesByIndex with Index is awkward...
      correct = this._cachedState.words.ok;
      let changedClues: Set<ClueModel> = new Set<ClueModel>();
      for (let change of changes) {
        for (let clue of this.getCluesByIndex(Index.create(change, this._cols))) {
          changedClues.add(clue);
        }
      }

      changedClues.forEach((clue: ClueModel) => {
        let current: boolean = this.isSolutionForClue(clue.squares, squares);
        let previous: boolean = this.isSolutionForClue(clue.squares, previousSolution);
        correct += (-toNumber(previous) + toNumber(current));
      });
    } else {
      correct = this._wordCount - this.getErrors(squares).size;
    }

    this._cachedState = {
      'solution': squares.join(''),
      'words': {
        'ok': correct,
        'total': this._wordCount
      }
    };

    return this._cachedState;
  }

  public getSquare(index: Index.t): SquareModel | null {
    return this._grid[index.value] || null;
  }

  public getAllClueSquares(): SquareModel[] {
    return this._grid.filter((sq: SquareModel) => {
      return sq.type === SquareType.Clues || sq.type === SquareType.Number;
    });
  }

  public getClueById(id: number): ClueModel | null {
    let clues: SquareModel[];
    let clue: ClueModel | null;
    let i: number;

    clues = this.getAllClueSquares();
    for (i = 0; i < clues.length; i++) {
      clue = (<CluesModel | NumberModel>clues[i]).getClueById(id);
      if (clue) {
        return clue;
      }
    }

    return null;
  }

  /**
   * Find a single clue from an index and a direction.
   * The index matches a clue as long as the index is contained within
   * the length of the clue.
   *
   * Note that this does not handle cases where multiple clues
   * cover some of the same squares in the same direction.
   */
  public getClueByIndex(index: Index.t, dir: Direction): ClueModel | null {
    let clues: ClueModel[];

    clues = this.getCluesByIndex(index).filter((c: ClueModel) => {
      return Direction.aligns(c.direction, dir);
    });

    return (clues.length >= 1) ? clues[0] : null;
  }

  /**
   * Returns all ClueModels that contain the given index,
   * independent of direction.
   */
  public getCluesByIndex(index: Index.t): ClueModel[] {
    // Build a cache on first run. The cache contains an entry
    // for each square in the grid, even if the square can never
    // contain a ClueModel. This way we narrow the return value
    // of `cache.get` to `ClueModel[]`, and can explicitly cast.
    let cache: Map<number, ClueModel[]>;

    if (this._cachedCluesByIndex === null) {
      cache = new Map<number, ClueModel[]>([
        ...<any>range(0, this.layout.length).map((x: number) => [x, []])
      ]);

      for (let square of this.getAllClueSquares()) {
        for (let clue of <ClueModel[]>square.value) {
          for (let index of <Index.t[]>clue.squares) {
            (<ClueModel[]>cache.get(index.value)).push(clue);
          }
        }
      }

      this._cachedCluesByIndex = cache;
    }

    return <ClueModel[]>this._cachedCluesByIndex.get(index.value);
  }

  public inBounds(index: Index.t): boolean {
    return index.value >= 0 && index.value < (this._rows * this._cols);
  }

  public isSelectableSquare(index: Index.t): boolean {
    return this.inBounds(index) && this._layout[index.value] === '_';
  }

  public isSolution(test: string[]): boolean {
    return this._solution.join('') === test.join('');
  }

  public isValidLetter(letter: string): boolean {
    return letter.length === 1
        && this._alphabet.indexOf(letter) !== -1;
  }

  public load(buffer: WordcrossFile.File): void {
    this._parse(buffer);
  }

  private _parse(buffer: WordcrossFile.File): void {
    this._wordCount = buffer.wordCount;
    this._rows = buffer.size.rows;
    this._cols = buffer.size.cols;
    this._setType(buffer.type);
    this._alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ0123456789';
    this._solution = buffer.solution;
    this._layout = buffer.solution.map((chr: string) => chr.replace(/[^\.]/g, '_'));

    [].forEach.call(buffer.grid, (square: WordcrossFile.Square) => {
      let value: SquareModel;
      let clue: WordcrossFile.Clues;
      let num: WordcrossFile.Number;
      let clues: ClueModel[];

      switch (square.type) {
        case 'Clues':
          clue = square.value;
          clues = [];
          [].forEach.call(clue.clues, (clue: WordcrossFile.Clue) => {
            clues.push(this._loadClue(clue, this._cols));
          });
          value = new CluesModel(clues);
          break;
        case 'Number':
          num = square.value;
          clues = [];
          [].forEach.call(num.clues, (clue: WordcrossFile.Clue) => {
            clues.push(this._loadClue(clue, this._cols));
          });
          value = new NumberModel(num.number, clues);
          break;
        case 'Block':
          value = new BlockModel();
          break;
        case 'Symbol':
          value = new SymbolModel(square.value.symbol);
          break;
        case 'Blank':
          value = new BlankModel();
          break;
        default:
          throw TypeError('Invalid node type: ' + square.type + '.');
      }

      this._grid[Index.from2D(square.row, square.col, this._cols).value] = value;
    });
  }

  private _setType(type: string): void {
    if (type === 'Regular') {
      this._type = CrosswordType.Regular;
    } else if (type === 'CluesInSquares') {
      this._type = CrosswordType.CluesInSquares;
    } else {
      throw new TypeError('Invalid crossword type: ' + type + '.');
    }
  }

  private _loadClue(clue: WordcrossFile.Clue, width: number): ClueModel {
    let dir: Direction;

    if (clue.direction === 'Vertical') {
      dir = Direction.Vertical;
    } else {
      dir = Direction.Horizontal;
    }

    return new ClueModel(
        clue.id, clue.clue, dir,
        Index.from2D(clue.origin.row, clue.origin.col, width),
        clue.length
    );
  }
}
